////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.2                                                            //
//  Summary: Axios instance — the one HTTP client this application uses      //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import axios, { AxiosError } from 'axios';

import cfg from '@/config';

const api = axios.create({
  baseURL: cfg.api.base,
  timeout: cfg.api.timeout_ms,
  // Admin auth is a session cookie (1.0.1.md §2.6 /auth/*), not a bearer token —
  // this must be true for the cookie to travel on cross-origin-shaped requests.
  withCredentials: true,
});

// CSRF (1.0.2.md §4.3/§4.6): `irn_csrf` is the non-HttpOnly half of the double-submit
// pair — the API mints it, this is the SPA's only job on it: read it back and mirror
// it into the header on every mutating request. Never stored in JS state; read fresh
// per request so a re-issued cookie (auth.ts `session()` re-derives it on every
// GET /auth/session) is picked up without a page reload.
const CSRF_COOKIE = 'irn_csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const SAFE_METHODS = new Set(['get', 'head', 'options']);

function readCookie (name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

api.interceptors.request.use((config) => {
  const method = (config.method ?? 'get').toLowerCase();
  if (!SAFE_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE);
    if (token) config.headers.set(CSRF_HEADER, token);
  }
  return config;
});

// Broadcast for "the session this request assumed is no longer valid" — a session
// revoked mid-visit (logout elsewhere, rbac downgrade, expiry) surfaces as a 401 on
// whatever admin call happens to run next, not just on the next explicit session
// check. hooks/useSession.ts subscribes to clear its local state; navigation back to
// /admin/login is the subscriber's job (it has router context, this module does not).
type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized (listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => { unauthorizedListeners.delete(listener); };
}

// Normalises every rejection into an Error with a message worth showing a user,
// without swallowing the original AxiosError (still available via `.cause`).
// hooks/useApi.ts and components/feedback/ErrorState.tsx are the consumers.
api.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (error instanceof AxiosError) {
      // GET /auth/session answering 401 is the routine "not signed in yet" case
      // (every unauthenticated mount of the admin subtree produces one) — broadcasting
      // it would fire the redirect-on-401 subscriber for a page that is, itself, the
      // login gate. Every OTHER 401 means a session that was assumed valid no longer
      // is, and that is genuinely new information worth broadcasting.
      if (error.response?.status === 401 && !error.config?.url?.endsWith('/auth/session')) {
        unauthorizedListeners.forEach((listener) => listener());
      }
      const message =
        (error.response?.data as { message?: string } | undefined)?.message ??
        error.message ??
        'Request failed';
      return Promise.reject(new Error(message, { cause: error }));
    }
    return Promise.reject(error instanceof Error ? error : new Error('Request failed', { cause: error }));
  },
);

export default api;
